import { defineStore } from 'pinia';
import { Breadcrumb } from '@/common/interfaces';
import { requests } from '@/common/plugins/axios';
import { Creative, CreativeMoveOptions,
  CreativeFolder, CreativeForm, CreativeGetOptions,
  CreativePlayTemplate, CreativeItem, CreativeLabels } from '@/views/creatives/interfaces';
import { sortItems, removeItemInArray, updateAxiosCache,
  removeFromAxiosCache, replaceAxiosCache } from '@/common/utils';
import { Orientation, ProductIds } from '@/common/enums';

import { useCompanyStore } from '@/common/store/company';
import { useHistoryStore } from '@/common/store/history';
import { CanvasTypes } from '@/views/creatives/enums';

const blankPlayCreative = {
  scenes: [],
  settings: {
    orientation: Orientation.PORTRAIT,
    orientationLock: Orientation.PORTRAIT,
    useBGAudio: false,
    BGAudio: {
      onAudioFin: 'None',
      url: '',
      playOnEndCard: true,
    },
    usePropagateURLs: true,
    propagateURLs: {
      onClickUrl: '',
      iosUrl: '',
      androidUrl: '',
    },
  },
};
// update to ref from creativeCanvasDetails.ts
const blankCanvasCreative = {
  boards: [],
  settings: {
    boardsToBeExported: [],
    variantBoardIdsToExclude: {},
    namingConvention: '{{Size Dimensions}}_{{Ad Name}}_{{Variant Name}}_{{Export Date}}',
  },
  data: {
    frames: [{
      id: 'F_Back',
      name: 'Background',
      type: 'background',
    }],
    instances: {
      F_Back: [
        {
          id: 'I_BG1',
          name: 'Background 1',
          type: CanvasTypes.BACKGROUND,
        },
      ],
    },
    iterations: {},
    variations: {},
    assets: {},
    orders: {},
  },
};

// small function to update incoming creative data with any missing values
const formatPlayData = (data: Creative) => {
  const clone = { ...data };

  // check if the creative is for play
  // if doesn't contain scenes, return
  if (!clone.scenes) return clone;

  // !TODO update this for nested children and only add btnEffect to font and image layers
  clone.scenes = clone.scenes.map((scene) => ({
    ...scene,
    children: scene?.children?.map((n) => ({ ...n, btnEffect: n.btnEffect || 'None' })),
  }));

  // corrects migrated playables orientation on load
  if (clone.settings.orientation !== clone.settings.orientationLock && clone.settings.orientationLock !== Orientation.LANDSCAPE_PORTRAIT) {
    clone.settings.orientation = clone.settings.orientationLock;
  }

  return clone;
};

export const useCreativeStore = defineStore('creative', {
  state: () => ({
    creatives: [] as Creative[],
    users: [] as { firstName: string; lastName: string, userId: string }[],
    currentCreative: {} as Creative,
    folders: [] as CreativeFolder[],
    playTemplates: [] as CreativePlayTemplate[],
    canvasCreatives: [] as Creative[],
    parentIds: JSON.parse(localStorage.getItem('cmp:ad-folders')) || [] as Breadcrumb[],
    favorites: [] as Creative[],
    favoriteIds: [],
    currentItemId: '',
    currentItemIndex: null,
    currentFolder: {} as CreativeFolder,
    cutItem: null, // the cut item to be used for pasting
    isSaving: false as string | boolean, // isSaving flag can be bool or string date
    showCreativeSettings: true, // show the settings panel for the current creative
    isGridMode: true,
    hasModeChanged: false,
    totalItems: 0,
    labels: {} as CreativeLabels,
    historyMode: false,
  }),

  getters: {
    currentParentId(state) {
      if (state.parentIds.length === 0) return 'null';
      const { length } = state.parentIds;
      return state.parentIds[length - 1].id;
    },
  },

  actions: {
    /**
     * Set new values to labels state.
     * @param folders - Folders value
     * @param ads - Ads value
     */
    setStateLabels(data: CreativeLabels) {
      Object.assign(this.labels, data);
    },

    /**
     * Fetch creatives from API and set to creatives
     * store variable. If the param name exists, filter
     * received data by name.
     * @param name: Creative name
     */
    async fetchCreatives(args: CreativeGetOptions, type?: 'deleted' | 'archived'): Promise<void> {
      let cacheRef = `fetch-creatives${type ? (`-${type}`) : ''}`;
      const companyId = useCompanyStore().currentCompanyId;

      if (args.folderId) {
        cacheRef = `${cacheRef}-${args.folderId}-${companyId}`;
      } else {
        cacheRef = `${cacheRef}-${companyId}`;
      }

      const result = await requests.get(
        `/creatives?includes=product&includes=groups:id${args.name ? `&q=name:contains:${args.name}` : ''}`,
        {
          id: `${cacheRef}${args.name ? (`:name:contains:${args.name}`) : ''}`,
          params: { ...args },
        },
      );

      this.creatives = result.items;
      this.folders = args.name ? [] : result.folders;

      if (!('order' in args)) {
        this.folders = sortItems(this.folders, { sortBy: 'updatedAt', desc: false });
        this.creatives = sortItems(this.creatives, { sortBy: 'updatedAt', desc: false });
      }

      this.totalItems = result.total;
    },

    async fetchCreativesOnScroll(args: CreativeGetOptions) {
      const result = await await requests.get(
        `/creatives?includes=product&includes=groups:id${args.name ? `&q=name:contains:${args.name}` : ''}`,
        {
          params: { ...args },
        },
      );

      if (result.items.length === 0) return;
      this.creatives.push(...result.items);
    },

    async fetchFolders(args: CreativeGetOptions) {
      const result = await requests.get(
        '/creatives?includes=product&includes=groups:id',
        {
          id: `fetch-folders-${args.folderId}`,
          params: { ...args },
        },
      );

      result.folders.sort((a, b) => a.name.localeCompare(b.name));

      return result.folders;
    },

    /**
     * Fetch currentCreative data by Id and set its data
     * to currentCreative state variable.
     * @param id => Creative id to fetch
     */
    async fetchCreativeById(id: string, historyId?: string): Promise<void> {
      const historyStore = useHistoryStore();

      // reset the history store if we are loading a different creative
      // !TODO $reset the actions if switching product
      if (id !== this.currentCreative.id) {
        historyStore.$reset();
        historyStore.commands = [];
      }

      const response: Creative = await requests.get(`/creatives/${id}?includes=product&includes=company:id`, {
        id: `fetch-creative-${id}`,
      });

      if (response.company) {
        const companyId = response.company.id;
        const companyStore = useCompanyStore();
        // Check currentCompanyId
        if (companyStore.currentCompanyId !== companyId) {
          await companyStore.selectCompany(companyId);
        }
      }

      if (this.historyMode) {
        const historyEntry = await requests.get(`/creatives/history/${historyId}?productId=${response.type.id}`);
        response.scenes = historyEntry.scenes;
        response.settings = historyEntry.settings;
      }

      this.currentCreative = formatPlayData(response);
    },

    /**
     * Calls 'selectCreative' action and set
     * @param creativeId => Company id
     */
    async editCreative(creativeId: string, historyId?: string): Promise<void> {
      this.selectCreative(creativeId);
      await this.fetchCreativeById(creativeId, historyId);
    },

    /**
     * Make a PATCH request to update any value of specific
     * creative data.
     * @param id => creative Id
     */
    async updateCreative(id: string): Promise<void> {
      await requests.patch(
        `/creatives/${id}`,
        this.currentCreative,
        {
          cache: {
            update: {
              'fetch-creatives': 'delete',
              [`fetch-creative-${id}`]: (cache, response) => replaceAxiosCache(cache, response),
            },
          },
        },
      );
    },

    /**
     * Make a PATCH request to update the current creative name
     * and update the response value with current value.
     * @param id => Creative id
     */
    async updateCreativeName(id: string, name: string) {
      const { coverImageS3Url, status, folderId } = this.currentCreative;
      const isFavorite = this.favoriteIds.some((fId) => fId === id);

      const response = await requests.patch(
        `/creatives/${id}`,
        { name, coverImageS3Url, status, folderId },
        {
          cache: {
            update: {
              'fetch-creatives': 'delete',
              [`fetch-creative-${id}`]: 'delete',
            },
          },
        },
      );

      const list = isFavorite ? this.favorites : this.creatives;
      const index = list.findIndex((n) => n.id === id);
      // Update
      list[index].name = response.name;
      list[index].updatedAt = response.updatedAt;
    },

    /**
     * Make PATCH request to update folder's name and update
     * the value in folders array.
     * @param id => FolderId to update
     */
    async updateFolderName(id: string, name: string) {
      const { parentId, companyId } = this.currentFolder;

      const response = await requests.patch(
        `creatives/folders/${id}`,
        { name, parentId, companyId },
        {
          cache: {
            update: {
              [`fetch-creatives-${parentId}-${companyId}`]: (cache, res) => updateAxiosCache(cache, res, 'folders', id),
              [`fetch-folders-${parentId}-${companyId}`]: (cache, res) => updateAxiosCache(cache, res, 'folders', id),
            },
          },
        },
      );

      // Update
      const index = this.folders.findIndex((n) => n.id === id);
      this.folders[index].name = response.name;
      this.folders[index].updatedAt = response.updatedAt;
    },

    /**
     * Set 'currentItemId' variable with folderId, search its
     *  index on folders array and update 'currentCreative' variable
     *  to show its data in rename action.
     * @param folderId => FolderId to select
     */
    selectFolder(folderId: string) {
      this.currentItemId = folderId;
      this.currentItemIndex = this.folders.findIndex((folder) => folder.id === folderId);
      this.currentFolder = this.folders[this.currentItemIndex];
    },

    /**
     * Make DELETE request to remove folder on
     * creative list and update 'folders' variable
     * after success response.
     * @param id => FolderId to delete
     */
    async deleteFolder(id: string) {
      await requests.delete(`/creatives/folders/${id}`);
      this.folders = removeItemInArray(this.folders, id) as CreativeFolder[];
    },

    /**
     * Set currentItemId store variable
     * creativeId value received.
     * @param creativeId => Creative id
     */
    selectCreative(creativeId: string): void {
      this.currentItemId = creativeId;
      this.currentItemIndex = this.creatives.findIndex((creative) => creative.id === creativeId);
    },

    /**
     * Fetch favorite creative ids and set to
     * favoriteIds store variable.
     */
    async fetchFavoriteCreatives(id: string, name?: string) {
      const result = await requests.get(`/me/favorites?includes=product&includes=creator&includes=groups:id&companies=${id}${name ? `&q=name:contains:${name}` : ''}`, {
        id: 'fetch-favorites',
      });

      this.favorites = result.items;
      this.favoriteIds = result.items.map((n: Creative) => n.id);
    },

    /**
     * Make a POST request to set a creative
     * to favorites group and add to favoriteIds array.
     * @param id => Creative id
     */
    async setCreativeAsFavorite(id: string) {
      await requests.post(
        `/creatives/${id}/favorite`,
        {},
        {
          cache: {
            update: {
              'fetch-favorites': 'delete',
            },
          },
        },
      );

      this.favoriteIds.push(id);
    },

    /**
     * Make a DELETE request to remove a creative
     * to favorites group and remove from favoriteIds array.
     * @param id => Creative id
     */
    async removeFromFavorites(id: string) {
      await requests.delete(
        `/creatives/${id}/favorite`,
        {
          cache: {
            update: {
              'fetch-favorites': 'delete',
            },
          },
        },
      );

      // Delete id from favorites
      this.favorites = this.favorites.filter((creative) => creative.id !== id);
      this.favoriteIds = this.favoriteIds.filter((creativeId) => creativeId !== id);
    },

    /**
     * Make a DELETE request to set a creative
     * to deleted group and add to deletedIds array.
     * @param id => Creative id
     * @param list => List to remove item
     */
    async deleteCreative(id: string, list: 'favorites' | 'creatives') {
      const companyId = useCompanyStore().currentCompanyId;
      await requests.delete(
        `/creatives/${id}`,
        {
          cache: {
            update: {
              [`fetch-creatives-${this.currentParentId}-${companyId}`]: 'delete',
              'fetch-creatives-deleted': 'delete',
              'fetch-creatives-archived': 'delete',
            },
          },
        },
      );
      // Remove in selected list
      this[list] = removeItemInArray(this[list], id) as Creative[];
    },

    /**
     * Make a POST request to set a creative
     * to archived group and add to archivedIds array.
     * @param id => Creative id
     * @param list => List to remove item
     */
    async archiveCreative(id: string, list: 'favorites' | 'creatives') {
      const companyId = useCompanyStore().currentCompanyId;
      await requests.post(
        `/creatives/${id}/archive`,
        {},
        {
          cache: {
            update: {
              [`fetch-creatives-${this.currentParentId}-${companyId}`]: 'delete',
              'fetch-creatives-archived': 'delete',
            },
          },
        },
      );
      // Remove in selected list
      this[list] = removeItemInArray(this[list], id) as Creative[];
    },

    /**
     * Make a POST request to restore a creative
     * from deleted or archived group.
     * @param id => Creative id
     */
    async restoreCreative(id: string) {
      const companyId = useCompanyStore().currentCompanyId;
      await requests.post(
        `/creatives/${id}/restore`,
        {},
        {
          cache: {
            update: {
              [`fetch-creatives-${this.currentParentId}-${companyId}`]: 'delete',
              'fetch-creatives-archived': 'delete',
              'fetch-creatives-deleted': 'delete',
            },
          },
        },
      );

      this.creatives = removeItemInArray(this.creatives, id) as Creative[];
    },

    /**
     * Make a DELETE request to confirm delete action in
     * specific creative to remove permanently
     * @param id => Creative id
     */
    async deleteCreativeConfirmation(id: string) {
      const companyId = useCompanyStore().currentCompanyId;
      await requests.delete(`/creatives/${id}?force=true`, {
        cache: {
          update: {
            [`fetch-creatives-${this.currentParentId}-${companyId}`]: 'delete',
            'fetch-creatives-archived': 'delete',
            'fetch-creatived-deleted': 'delete',
          },
        },
      });

      this.creatives = removeItemInArray(this.creatives, id) as Creative[];
    },

    /**
     * Make a GET request to fetch play creative templates
     * to select in case of create a new creative and assign
     * to templates variable.
     */
    async fetchPlayCreativeTemplates() {
      const companyId = useCompanyStore().currentCompanyId;

      // use the companyId to get templates from V1 that are only relevant for this company
      const response = await requests.get(`/types/templates?companyId=${companyId}`, {
        id: `fetch-play-creative-templates-${companyId}`,
      });

      this.playTemplates = response.items;
    },

    /**
     * Make a GET request to fetch canvas creatives
     * to select in case of create a new creative and assign.
     */
    async fetchCanvasCreatives() {
      const companyStore = useCompanyStore();
      const companyId = companyStore.currentCompanyId;
      const response = await requests.get(`/creatives?type=${ProductIds.CANVAS}&companies=${companyId}`, {
        id: `fetch-canvas-creatives-${companyId}`,
      });

      this.canvasCreatives = response.items;
    },

    /**
     * Make a POST request to create a new creative, add the
     * creative to creatives state variable.
     */
    async createCreative(data: CreativeForm) {
      let body = { ...data };
      const companyId = useCompanyStore().currentCompanyId;

      // use the default settings for a play creative
      if (data.productId === ProductIds.PLAY) {
        body = {
          ...data,
          ...blankPlayCreative,
        };

      // use the default settings for a static creative
      } else if (data.productId === ProductIds.CANVAS) {
        body = {
          ...data,
          ...blankCanvasCreative,
        };
      }

      const result = await requests.post('/creatives?includes=creator', body, {
        cache: {
          update: {
            [`fetch-creatives-${this.currentParentId}-${companyId}`]: (cache, response) => updateAxiosCache(cache, response, 'items', response.data.id),
          },
        },
      });

      this.creatives.unshift(result);

      return result;
    },

    /**
     * Make a POST request to duplicate current creative and
     * add created item in current creatives array.
     * @param id => Creative Id
     */
    async duplicateCreative(id: string) {
      const parentId = (this.parentIds.length > 0) ? this.parentIds.at(-1).id : null;
      const companyId = useCompanyStore().currentCompanyId;
      const currentItemIndex = this.creatives.findIndex((item) => item.id === id);
      const result = await requests.post(`/creatives/${id}/duplicate?includes=creator`, {}, {
        cache: {
          update: {
            [`fetch-creatives-${parentId}-${companyId}`]: (cache, response) => updateAxiosCache(cache, response, 'items', response.data.id),
          },
        },
      });

      // Insert underneath of currentItem
      this.creatives.splice(currentItemIndex + 1, 0, result);

      return result;
    },

    /**
     * Make a POST request to create a new creative folder, add the
     * folder to creatives state variable.
     */
    async createCreativeFolder(data: Partial<CreativeFolder>) {
      const companyId = useCompanyStore().currentCompanyId;
      const result = await requests.post('/creatives/folders?includes=creator', data, {
        cache: {
          update: {
            [`fetch-creatives-${data.parentId}-${companyId}`]: (cache, response) => updateAxiosCache(cache, response, 'folders', response.data.id),
            [`fetch-folders-${data.parentId}-${companyId}`]: (cache, response) => updateAxiosCache(cache, response, 'folders', response.data.id),
          },
        },
      });

      if (this.currentParentId === data.parentId) {
        // Add to the top
        this.folders.unshift(result);
      }

      return result;
    },

    /**
     * Make a POST request to share specific creative to
     * specific groups to allow to will be seen by users
     * that be in these groups.
     * @param id => Creative id
     * @param groups => Group ids
     */
    async shareCreative(id: string, groups: string[]) {
      await requests.post(`/creatives/${id}/share`, { groups });
    },

    /**
     * Add new parentId value as breadcrumb item to
     * show in Breadcrumb component.
     * @param item => New breadcrumb item.
     */
    pushNewFolder(item: Partial<CreativeFolder>) {
      this.parentIds.push({
        label: item.name,
        id: item.id,
        to: {
          name: 'CreativeListInFolderView',
          params: { id: item.id },
        },
      });
      // Set folders to localStorage
      localStorage.setItem('cmp:ad-folders', JSON.stringify(this.parentIds));
    },

    /**
     * Delete all items on parentIds variable.
     */
    resetParentFolders() {
      this.parentIds.length = 0;
      this.currentFolder = {} as CreativeFolder;
      this.parentIds = [];
      localStorage.setItem('cmp:ad-folders', JSON.stringify([]));
    },

    /**
     * Create new array from parentIds starting on
     * 0 index and finishing in receiving value.
     * @param index => Last item on new subarray
     */
    removeParentFolderIndex(index: number) {
      this.parentIds = this.parentIds.slice(0, index + 1);
    },

    /**
     * Move Creative Item to different folder and update
     * axios-cache with new data (destinationId - currentFolderId).
     * @param data => CreativeMoveOptions
     */
    async moveItem(data: CreativeMoveOptions) {
      const companyId = useCompanyStore().currentCompanyId;
      const list = data.isFolder ? 'folders' : 'items';
      const endpoint = data.isFolder ? 'creatives/folders' : 'creatives';
      const body = data.isFolder ? { parentId: data.destinationId } : { folderId: data.destinationId };

      await requests.patch(`/${endpoint}/${data.itemId}`, body, {
        cache: {
          update: {
            [`fetch-creatives-${data.currentParent}-${companyId}`]: (cache) => removeFromAxiosCache(cache, list, data.itemId),
            [`fetch-creatives-${data.destinationId}-${companyId}`]: (cache, response) => updateAxiosCache(cache, response, list, response.data.id),
            [`fetch-folders-${data.destinationId}-${companyId}`]: (cache, response) => {
              if (cache.state !== 'cached' || !data.isFolder) return 'ignore';
              // Add folder to destinationId
              return updateAxiosCache(cache, response, 'folders', response.data.id);
            },
            [`fetch-folders-${data.currentParent}`]: (cache) => {
              if (cache.state !== 'cached' || !data.isFolder) return 'ignore';
              // Remove to current cache
              return removeFromAxiosCache(cache, list, data.itemId);
            },
            [`fetch-folders-${data.itemId}`]: 'delete',
          },
        },
      });

      // Update 'DateModified' on destination folder
      const destinationIndex = this.folders.findIndex((f) => f.id === data.destinationId);
      if (destinationIndex !== -1) this.folders[destinationIndex].updatedAt = new Date(Date.now()).getTime();

      // Remove item from current state array.
      if (data.isFolder) {
        this.folders = removeItemInArray(this.folders, data.itemId) as CreativeFolder[];
      } else {
        this.creatives = removeItemInArray(this.creatives, data.itemId) as Creative[];
      }
    },

    /**
     * Make a PATCH request to transfer a creative
     * to specific company, remove it from current
     * list of creatives and update fetch-creatives cache.
     * @param creative => Creative item
     * @param companyId => Destination company
     */
    async transferCreative(creative: CreativeItem, companyId: string) {
      await requests.patch(
        `/creatives/${creative.id}`,
        { companyId, folderId: 'null' },
        {
          cache: {
            update: {
              [`fetch-creatives-${creative.parentId}`]: (cache) => removeFromAxiosCache(cache, 'items', creative.id),
            },
          },
        },
      );
      // Remove from current creatives array
      const index = this.creatives.findIndex((c) => c.id === creative.id);
      this.creatives.splice(index, 1);
    },

    /**
     * Update isGridMode to manage grid mode
     * in Creative routes.
     */
    setCreativesViewValue(value: boolean) {
      this.hasModeChanged = !value;
      this.isGridMode = value;
    },
  },
});

export default useCreativeStore;
